import React, { useState, useEffect, useRef } from 'react';
import { Hero } from '../../../features/home/components/Hero';
import { PopularPackages } from '../../components/marketing/PopularPackages';
import { Features } from '../../../features/home/components/Features';
import { Testimonials } from '../../../features/home/components/Testimonials';
import { Newsletter } from '../../../features/home/components/Newsletter';
import { VideoSection } from '../../../features/home/components/VideoSection';
import { UserTypeSection } from '../../../features/home/components/UserInfoSection';
import { FloatingSearchBox } from '../../components/ui';
import { isMobile } from '../../utils/deviceUtils';
import 'keen-slider/keen-slider.min.css';

// New reveal scroll hook
const useRevealOnScroll = (threshold: number = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsVisible(true);
          setHasAnimated(true);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [threshold, hasAnimated]);

  return { isVisible, elementRef };
};

// Reveal section wrapper component
const RevealSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right';
}> = ({ children, className = '', delay = 0, direction = 'up' }) => {
  const { isVisible, elementRef } = useRevealOnScroll();
  const mobile = isMobile();

  const getTransform = () => {
    // Reduce animation distance on mobile devices
    const distance = mobile ? '30px' : '50px';

    switch (direction) {
      case 'up':
        return `translateY(${distance})`;
      case 'down':
        return `translateY(-${distance})`;
      case 'left':
        return `translateX(${distance})`;
      case 'right':
        return `translateX(-${distance})`;
      default:
        return `translateY(${distance})`;
    }
  };

  return (
    <div
      ref={elementRef}
      className={`transition-all duration-500 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? 'translateY(0) scale(1)'
          : `${getTransform()} scale(0.95)`,
        transitionDelay: `${delay}ms`,
        // Faster animation duration for snappier reveals
        transitionDuration: mobile ? '300ms' : '500ms',
      }}
    >
      {children}
    </div>
  );
};



export const Home: React.FC = () => {
  const mobile = isMobile();

  return (
    <div className="min-h-screen">
      {/* Hero Section - No reveal, stays fixed */}
      <Hero />

      {/* Floating Search Box - Positioned to blend sections */}
      <FloatingSearchBox />

      {/* User Type Section - Reveal from bottom */}
      <RevealSection direction="up" delay={mobile ? 0 : 0}>
        <UserTypeSection />
      </RevealSection>

      {/* Popular Packages Section - Reveal from bottom */}
      <RevealSection direction="up" delay={mobile ? 0 : 0}>
        <PopularPackages />
      </RevealSection>

      {/* Features Section - Reveal from left on desktop/tablet, up on mobile */}
      <RevealSection
        direction={mobile ? 'up' : 'left'}
        delay={mobile ? 0 : 0}
      >
        <Features />
      </RevealSection>

      {/* Video Section - Reveal from right on desktop/tablet, up on mobile */}
      <RevealSection
        direction={mobile ? 'up' : 'right'}
        delay={mobile ? 0 : 0}
      >
        <VideoSection />
      </RevealSection>

      {/* Testimonials Section - Reveal from bottom */}
      <RevealSection direction="up" delay={mobile ? 0 : 0}>
        <Testimonials />
      </RevealSection>

      {/* Newsletter Section - Reveal from bottom */}
      <RevealSection direction="up" delay={mobile ? 0 : 0}>
        <Newsletter />
      </RevealSection>
    </div>
  );
};
